import { RetainerState, ConsentFormState } from '../types';

/**
 * Configuration for document validation
 */
export interface DocumentValidationConfig {
  documentName: string;
  isRequired: boolean;
  isUploadSelected: boolean;
  isTextSelected: boolean;
  isDefaultSelected: boolean;
  uploadedFile: File | null;
  textContent: string | null;
}

/**
 * Generic document validation result
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a document based on configuration
 * @param config - Document validation configuration
 * @returns Validation result with isValid flag and optional error message
 */
export function validateDocument(config: DocumentValidationConfig): ValidationResult {
  const { 
    documentName, 
    isRequired, 
    isUploadSelected, 
    isTextSelected, 
    isDefaultSelected, 
    uploadedFile, 
    textContent 
  } = config;

  // Check if document is required but no option selected
  if (isRequired && !isUploadSelected && !isTextSelected && !isDefaultSelected) {
    return { 
      isValid: false, 
      error: `${documentName} is required but no option is selected.` 
    };
  }

  // Validate text-based document
  if (isTextSelected) {
    if (textContent === "" || textContent === null) {
      return { 
        isValid: false, 
        error: `${documentName} text cannot be empty.` 
      };
    }
  }

  // Validate uploaded document
  if (isUploadSelected) {
    if (!uploadedFile) {
      return { 
        isValid: false, 
        error: `Please upload a ${documentName.toLowerCase()} file.` 
      };
    }
    if (uploadedFile.type !== "application/pdf") {
      return { 
        isValid: false, 
        error: `You can only upload ${documentName} in PDF format.` 
      };
    }
  }

  return { isValid: true };
}

/**
 * Validates retainer document state
 * @param retainerState - Current retainer state
 * @param uploadedRetainerFile - Uploaded retainer file (if any)
 * @returns Validation result
 */
export function validateRetainer(
  retainerState: RetainerState, 
  uploadedRetainerFile: File | null
): ValidationResult {
  return validateDocument({
    documentName: "Retainer",
    isRequired: retainerState.retainerRequired,
    isUploadSelected: retainerState.uploadRetainer,
    isTextSelected: retainerState.textBoxRetainerRequired,
    isDefaultSelected: retainerState.defaultRetainerRequired,
    uploadedFile: uploadedRetainerFile,
    textContent: retainerState.textBasedRetainer
  });
}

/**
 * Validates consent form document state
 * @param consentFormState - Current consent form state
 * @param uploadedConsentFile - Uploaded consent file (if any)
 * @returns Validation result
 */
export function validateConsentForm(
  consentFormState: ConsentFormState, 
  uploadedConsentFile: File | null
): ValidationResult {
  return validateDocument({
    documentName: "Consent form",
    isRequired: consentFormState.consentFormRequired,
    isUploadSelected: consentFormState.uploadConsentForm,
    isTextSelected: consentFormState.textBoxConsentFormRequired,
    isDefaultSelected: consentFormState.defaultConsentFormRequired,
    uploadedFile: uploadedConsentFile,
    textContent: consentFormState.textBasedConsentForm
  });
}

